import React, { useMemo, useState } from 'react';
import { ScrollTextIcon, MapPinIcon, DollarSignIcon, ShoppingCartIcon, LogInIcon, LogOutIcon, SearchIcon, FilterIcon, CalendarIcon, UserIcon, NavigationIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../context/AuthContext';
import { ActivityLog } from '../types';
function getActionIcon(action: string) {
  const a = action.toLowerCase();
  if (a.includes('visita')) return <MapPinIcon className="w-4 h-4" data-id="element-313" />;
  if (a.includes('cobro') || a.includes('pago')) return <DollarSignIcon className="w-4 h-4" data-id="element-314" />;
  if (a.includes('venta') || a.includes('pedido')) return <ShoppingCartIcon className="w-4 h-4" data-id="element-315" />;
  if (a.includes('inicio de sesión')) return <LogInIcon className="w-4 h-4" data-id="element-316" />;
  if (a.includes('cierre de sesión')) return <LogOutIcon className="w-4 h-4" data-id="element-317" />;
  if (a.includes('factura') || a.includes('nota de crédito') || a.includes('nc')) return <DollarSignIcon className="w-4 h-4" data-id="element-318" />;
  if (a.includes('producto') || a.includes('inventario')) return <ShoppingCartIcon className="w-4 h-4" data-id="element-319" />;
  if (a.includes('planificación') || a.includes('televentas')) return <ClockIcon className="w-4 h-4" data-id="element-320" />;
  if (a.includes('usuario') || a.includes('rol') || a.includes('comision')) return <UserIcon className="w-4 h-4" data-id="element-321" />;
  if (a.includes('farmacia') || a.includes('caja vacía')) return <MapPinIcon className="w-4 h-4" data-id="element-322" />;
  return <ScrollTextIcon className="w-4 h-4" data-id="element-323" />;
}
function getActionColor(action: string) {
  const a = action.toLowerCase();
  if (a.includes('visita') && (a.includes('inicio') || a.includes('inicio'))) return {
    bg: 'bg-indigo-100',
    text: 'text-indigo-600',
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  };
  if (a.includes('visita') && a.includes('fin')) return {
    bg: 'bg-emerald-100',
    text: 'text-emerald-600',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  };
  if (a.includes('visita')) return {
    bg: 'bg-indigo-100',
    text: 'text-indigo-600',
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  };
  if (a.includes('cobro')) return {
    bg: 'bg-emerald-100',
    text: 'text-emerald-600',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  };
  if (a.includes('venta')) return {
    bg: 'bg-blue-100',
    text: 'text-blue-600',
    dot: 'bg-blue-500',
    badge: 'bg-blue-50 text-blue-700 border-blue-200'
  };
  if (a.includes('inicio de sesión')) return {
    bg: 'bg-violet-100',
    text: 'text-violet-600',
    dot: 'bg-violet-500',
    badge: 'bg-violet-50 text-violet-700 border-violet-200'
  };
  if (a.includes('cierre de sesión')) return {
    bg: 'bg-rose-100',
    text: 'text-rose-600',
    dot: 'bg-rose-500',
    badge: 'bg-rose-50 text-rose-700 border-rose-200'
  };
  return {
    bg: 'bg-slate-100',
    text: 'text-slate-600',
    dot: 'bg-slate-400',
    badge: 'bg-slate-50 text-slate-700 border-slate-200'
  };
}
function formatDateTime(date: Date) {
  const d = new Date(date);
  return {
    date: d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }),
    time: d.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    })
  };
}
function groupByDate(logs: ActivityLog[]) {
  const groups: Record<string, ActivityLog[]> = {};
  logs.forEach(log => {
    const d = new Date(log.timestamp);
    const key = d.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
    if (!groups[key]) groups[key] = [];
    groups[key].push(log);
  });
  return groups;
}
export function BitacoraPage() {
  const {
    activityLogs,
    user
  } = useAuth();
  const [search, setSearch] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterCategory, setFilterCategory] = useState<'todos' | 'visitas' | 'crud' | 'sesiones'>('todos');
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const isAdmin = user?.rolNombre === 'Administrador';
  const getCategoryFromAction = (action: string) => {
    const a = action.toLowerCase();
    if (a.includes('visita')) return 'visitas';
    if (a.includes('sesión') || a.includes('sesion') || a.includes('inicio de ses') || a.includes('cierre de ses')) return 'sesiones';
    return 'crud';
  };
  // Unique users for filter
  const uniqueUsers = useMemo(() => {
    const users = [...new Set(activityLogs.map(l => l.userName))];
    return users.sort();
  }, [activityLogs]);
  // Show ALL activity logs including login/logout sessions
  const auditLogs = activityLogs;
  const filtered = useMemo(() => {
    return auditLogs.filter(log => {
      const matchSearch = !search || log.action.toLowerCase().includes(search.toLowerCase()) || log.userName.toLowerCase().includes(search.toLowerCase()) || log.details.toLowerCase().includes(search.toLowerCase());
      const matchUser = !filterUser || log.userName === filterUser;
      const matchCategory = filterCategory === 'todos' || getCategoryFromAction(log.action) === filterCategory;
      return matchSearch && matchUser && matchCategory;
    });
  }, [auditLogs, search, filterUser, filterCategory]);
  const grouped = useMemo(() => groupByDate(filtered), [filtered]);
  const today = new Date().toDateString();
  const todayCount = auditLogs.filter(l => new Date(l.timestamp).toDateString() === today).length;
  const withGps = auditLogs.filter(l => l.location).length;
  const crudCount = auditLogs.filter(l => getCategoryFromAction(l.action) === 'crud').length;
  const visitCount = auditLogs.filter(l => getCategoryFromAction(l.action) === 'visitas').length;
  const sessionCount = auditLogs.filter(l => getCategoryFromAction(l.action) === 'sesiones').length;
  const categoryTabs = [{
    id: 'todos',
    label: 'Todos',
    count: auditLogs.length
  }, {
    id: 'visitas',
    label: 'Visitas',
    count: visitCount
  }, {
    id: 'crud',
    label: 'Cambios',
    count: crudCount
  }, {
    id: 'sesiones',
    label: 'Sesiones',
    count: sessionCount
  }] as const;
  return <div className="space-y-6" data-id="element-324">
      <PageHeader title="Bitácora del Sistema" subtitle="Registro de actividades y auditoría" icon={<ScrollTextIcon className="w-6 h-6" data-id="element-326" />} data-id="element-325">
        <div className="flex items-center gap-2 px-4 py-2 bg-[#eef2fc] border border-[#d5e0f7] rounded-xl" data-id="element-327">
          <ScrollTextIcon className="w-4 h-4 text-[#2952C4]" data-id="element-328" />
          <span className="text-sm font-bold text-[#1E3D96]" data-id="element-329">
            {activityLogs.length}
          </span>
          <span className="text-sm text-[#2952C4]" data-id="element-330">registros totales</span>
        </div>
      </PageHeader>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-stagger-1" data-id="element-331">
        {[{
        label: 'Total registros',
        value: auditLogs.length,
        icon: <ScrollTextIcon className="w-5 h-5" data-id="element-332" />,
        color: 'text-brand-600 bg-brand-100'
      }, {
        label: 'Hoy',
        value: todayCount,
        icon: <CalendarIcon className="w-5 h-5" data-id="element-333" />,
        color: 'text-emerald-600 bg-emerald-100'
      }, {
        label: 'Cambios CRUD',
        value: crudCount,
        icon: <NavigationIcon className="w-5 h-5" data-id="element-334" />,
        color: 'text-amber-600 bg-amber-100'
      }, {
        label: 'Usuarios activos',
        value: uniqueUsers.length,
        icon: <UserIcon className="w-5 h-5" data-id="element-335" />,
        color: 'text-violet-600 bg-violet-100'
      }].map(stat => <div key={stat.label} className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-4 flex items-center gap-3" data-id="element-336">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${stat.color}`} data-id="element-337">
              {stat.icon}
            </div>
            <div data-id="element-338">
              <p className="text-xl font-bold text-slate-900 font-heading" data-id="element-339">
                {stat.value}
              </p>
              <p className="text-xs text-slate-500" data-id="element-340">{stat.label}</p>
            </div>
          </div>)}
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 flex-wrap animate-stagger-2" data-id="element-341">
        {categoryTabs.map(tab => <button key={tab.id} onClick={() => setFilterCategory(tab.id)} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${filterCategory === tab.id ? 'bg-[#2952C4] text-white shadow-md' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#2952C4]/30 hover:text-[#2952C4]'}`} data-id="element-342">
            {tab.label}
            <span className={`ml-1.5 text-xs ${filterCategory === tab.id ? 'text-white/70' : 'text-slate-400'}`} data-id="element-343">
              {tab.count}
            </span>
          </button>)}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-4 space-y-3" data-id="element-344">
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-345">
          <div className="relative flex-1" data-id="element-346">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-347" />
            <input type="text" placeholder="Buscar por acción, usuario o detalle..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] focus:bg-white transition-all" data-id="element-348" />
          </div>
          {isAdmin && <select value={filterUser} onChange={e => setFilterUser(e.target.value)} className="px-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] transition-all text-slate-700" data-id="element-349">
              <option value="" data-id="element-350">Todos los usuarios</option>
              {uniqueUsers.map(u => <option key={u} value={u} data-id="element-351">
                  {u}
                </option>)}
            </select>}
        </div>

        {(search || filterUser) && <div className="flex items-center gap-2" data-id="element-352">
            <span className="text-xs text-slate-500" data-id="element-353">
              {filtered.length} resultado(s)
            </span>
            <button onClick={() => {
          setSearch('');
          setFilterUser('');
        }} className="text-xs text-[#2952C4] hover:text-[#1E3D96] font-medium" data-id="element-354">
              Limpiar filtros
            </button>
          </div>}
      </div>

      {/* Activity List */}
      <div className="space-y-4 animate-stagger-3" data-id="element-355">
        {Object.entries(grouped).length === 0 ? <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] py-16 text-center animate-fade-in" data-id="element-356">
            <ScrollTextIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" data-id="element-357" />
            <p className="text-slate-500 font-medium" data-id="element-358">No hay registros</p>
            <p className="text-sm text-slate-400 mt-1" data-id="element-359">
              Prueba con otros filtros
            </p>
          </div> : Object.entries(grouped).map(([dateLabel, logs]) => <div key={dateLabel} data-id="element-360">
              <div className="flex items-center gap-3 mb-4" data-id="element-361">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-xl" data-id="element-362">
                  <CalendarIcon className="w-3.5 h-3.5 text-slate-500" data-id="element-363" />
                  <span className="text-xs font-semibold text-slate-600 capitalize font-heading" data-id="element-364">
                    {dateLabel}
                  </span>
                </div>
                <div className="flex-1 h-px bg-slate-100" data-id="element-365" />
                <span className="text-xs text-slate-400" data-id="element-366">
                  {logs.length} eventos
                </span>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden divide-y divide-slate-50" data-id="element-367">
                {logs.map(log => {
            const colors = getActionColor(log.action);
            const {
              date,
              time
            } = formatDateTime(log.timestamp);
            const isExpanded = expandedLog === log.id;
            return <div key={log.id} className="group" data-id="element-368">
                      <button onClick={() => setExpandedLog(isExpanded ? null : log.id)} className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left" data-id="element-369">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${colors.bg} ${colors.text}`} data-id="element-370">
                          {getActionIcon(log.action)}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-371">
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-372">
                            <span className="text-sm font-semibold text-slate-900" data-id="element-373">
                              {log.action}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${colors.badge}`} data-id="element-374">
                              {log.userName}
                            </span>
                          </div>
                          {log.details && <p className="text-xs text-slate-500 mt-0.5 truncate" data-id="element-375">
                              {log.details}
                            </p>}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-376">
                          {log.location && <div className="hidden sm:flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-lg" data-id="element-377">
                              <NavigationIcon className="w-3 h-3 text-emerald-600" data-id="element-378" />
                              <span className="text-xs text-emerald-700 font-medium" data-id="element-379">
                                GPS
                              </span>
                            </div>}
                          <div className="text-right" data-id="element-380">
                            <p className="text-xs font-semibold text-slate-700" data-id="element-381">
                              {time}
                            </p>
                            <p className="text-xs text-slate-400" data-id="element-382">{date}</p>
                          </div>
                          <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-383" />
                        </div>
                      </button>

                      {isExpanded && <div className="px-5 pb-4 bg-slate-50 border-t border-slate-100" data-id="element-384">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4" data-id="element-385">
                            <div data-id="element-386">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-387">
                                Usuario
                              </p>
                              <p className="text-sm text-slate-900 font-medium" data-id="element-388">
                                {log.userName}
                              </p>
                            </div>
                            <div data-id="element-389">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-390">
                                Fecha y Hora
                              </p>
                              <p className="text-sm text-slate-900 font-medium" data-id="element-391">
                                {date} a las {time}
                              </p>
                            </div>
                            {log.details && <div className="sm:col-span-2" data-id="element-392">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-393">
                                  Detalles
                                </p>
                                <p className="text-sm text-slate-900" data-id="element-394">
                                  {log.details}
                                </p>
                              </div>}
                            {log.location && <div className="sm:col-span-2" data-id="element-395">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-396">
                                  Ubicación GPS
                                </p>
                                <div className="flex items-center gap-2" data-id="element-397">
                                  <NavigationIcon className="w-4 h-4 text-emerald-600" data-id="element-398" />
                                  <span className="text-sm text-slate-900 font-mono" data-id="element-399">
                                    {log.location.lat.toFixed(6)},{' '}
                                    {log.location.lng.toFixed(6)}
                                  </span>
                                </div>
                              </div>}
                          </div>
                        </div>}
                    </div>;
          })}
              </div>
            </div>)}
      </div>
    </div>;
}